// ZhaSearch Native Python FastAPI Client

const API_BASE_URL = typeof window !== 'undefined' 
  ? (window.location.origin.includes('localhost') ? 'https://zhasearch.com/api/v1' : '/api/v1')
  : 'https://zhasearch.com/api/v1';

export async function apiRequest<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  const token = typeof window !== 'undefined' ? localStorage.getItem('zhasearch_admin_jwt') : null;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {})
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers
  });

  if (!response.ok) {
    let errorDetail = 'API request failed';
    try {
      const errJson = await response.json();
      errorDetail = errJson.detail || errorDetail;
    } catch {}
    throw new Error(errorDetail);
  }

  return response.json();
}

// 1. Listings API
export const listingsApi = {
  getListings: async (params: {
    q?: string;
    city?: string;
    category?: string;
    status?: string;
    is_elite?: boolean;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    page?: number;
    page_size?: number;
  } = {}) => {
    const query = new URLSearchParams();
    if (params.q) query.set('q', params.q);
    if (params.city) query.set('city', params.city);
    if (params.category) query.set('category', params.category);
    if (params.status) query.set('status', params.status);
    if (params.is_elite !== undefined) query.set('is_elite', String(params.is_elite));
    if (params.sort_by) query.set('sort_by', params.sort_by);
    if (params.sort_order) query.set('sort_order', params.sort_order);
    if (params.page) query.set('page', String(params.page));
    if (params.page_size) query.set('page_size', String(params.page_size));

    return apiRequest(`/listings?${query.toString()}`);
  },

  getListingBySlugOrId: async (slugOrId: string) => {
    return apiRequest(`/listings/${encodeURIComponent(slugOrId)}`);
  },

  toggleElite: async (id: string) => {
    return apiRequest(`/listings/${encodeURIComponent(id)}/elite`, { method: 'PATCH' });
  },

  updateListing: async (idOrSlug: string, updates: any) => {
    return apiRequest(`/listings/${encodeURIComponent(idOrSlug)}`, {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });
  },

  trackClick: async (id: string, clickType: 'call' | 'whatsapp' | 'direction' | 'enquiry') => {
    return apiRequest(`/listings/${encodeURIComponent(id)}/click?click_type=${clickType}`, { method: 'PATCH' });
  },

  batchCreateListings: async (items: any[]) => {
    return apiRequest('/listings/batch', {
      method: 'POST',
      body: JSON.stringify(items)
    });
  },

  recordClick: async (id: string, type: 'call' | 'whatsapp' | 'direction' | 'enquiry') => {
    return apiRequest(`/listings/${id}/click?click_type=${type}`, {
      method: 'POST'
    });
  }
};

// 2. Blogs API
export const blogsApi = {
  getBlogs: async (params: {
    status?: string;
    category?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    page?: number;
    page_size?: number;
  } = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.category) query.set('category', params.category);
    if (params.sort_by) query.set('sort_by', params.sort_by);
    if (params.sort_order) query.set('sort_order', params.sort_order);
    if (params.page) query.set('page', String(params.page));
    if (params.page_size) query.set('page_size', String(params.page_size));

    return apiRequest(`/blogs?${query.toString()}`);
  },

  getBlogBySlug: async (slug: string) => {
    return apiRequest(`/blogs/${encodeURIComponent(slug)}`);
  },

  createBlog: async (data: any) => {
    return apiRequest('/blogs', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateBlog: async (id: string, data: any) => {
    return apiRequest(`/blogs/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteBlog: async (id: string) => {
    return apiRequest(`/blogs/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  },

  bulkAction: async (ids: string[], action: 'publish' | 'draft' | 'delete') => {
    return apiRequest('/blogs/bulk', {
      method: 'POST',
      body: JSON.stringify({ ids, action })
    });
  }
};

// 3. Leads API
export const leadsApi = {
  submitLead: async (data: {
    listing_id?: string;
    customer_name: string;
    customer_phone: string;
    customer_email?: string;
    service_needed?: string;
    city?: string;
  }) => {
    return apiRequest('/leads', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  createLead: async (data: {
    listing_id?: string;
    customer_name: string;
    customer_phone: string;
    customer_email?: string;
    service_needed?: string;
    city?: string;
  }) => {
    return apiRequest('/leads', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  getLeads: async (page = 1, pageSize = 20) => {
    return apiRequest(`/leads?page=${page}&page_size=${pageSize}`);
  }
};

// 4. Auth API
export const authApi = {
  login: async (email: string, password: string) => {
    const res = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
    if (res.access_token && typeof window !== 'undefined') {
      localStorage.setItem('zhasearch_admin_jwt', res.access_token);
      localStorage.setItem('localhub_admin_email', res.email);
    }
    return res;
  },

  logout: () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('zhasearch_admin_jwt');
      localStorage.removeItem('localhub_admin_email');
    }
  }
};

// 5. Categories API
export const categoriesApi = {
  getCategories: async (): Promise<{ id: string; name: string; slug: string }[]> => {
    return apiRequest('/categories');
  },
  getCategoryStats: async (): Promise<Record<string, number>> => {
    return apiRequest('/categories/stats');
  },
  createCategory: async (category: { name: string; slug?: string }) => {
    return apiRequest('/categories', {
      method: 'POST',
      body: JSON.stringify(category)
    });
  },
  deleteCategory: async (idOrSlug: string) => {
    return apiRequest(`/categories/${encodeURIComponent(idOrSlug)}`, {
      method: 'DELETE'
    });
  }
};

// 6. Analytics & Visitor Events API
export const analyticsApi = {
  recordEvent: async (data: {
    event_type: string;
    listing_id?: string;
    city?: string;
    category?: string;
    meta?: any;
  }) => {
    return apiRequest('/analytics/events', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  getEvents: async (params: { event_type?: string; city?: string; page?: number; page_size?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.event_type) query.set('event_type', params.event_type);
    if (params.city) query.set('city', params.city);
    if (params.page) query.set('page', String(params.page));
    if (params.page_size) query.set('page_size', String(params.page_size));
    return apiRequest(`/analytics/events?${query.toString()}`);
  },

  getStats: async () => {
    return apiRequest('/analytics/stats');
  }
};

// 7. Real Estate Developer Platform API
export const developersApi = {
  register: (data: { company_name: string; email: string; password: string; phone?: string; rera_reg_no?: string; website?: string; headquarters?: string }) => 
    apiRequest('/developers/register', { method: 'POST', body: JSON.stringify(data) }),
  
  login: (data: { email: string; password: string }) => 
    apiRequest('/developers/login', { method: 'POST', body: JSON.stringify(data) }),
  
  getMe: (token?: string) => 
    apiRequest('/developers/me', { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
  
  updateMe: (data: any, token?: string) => 
    apiRequest('/developers/me', { method: 'PUT', body: JSON.stringify(data), headers: token ? { Authorization: `Bearer ${token}` } : {} }),
  
  getMyProjects: (token?: string) => 
    apiRequest('/developers/my-projects', { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
  
  createMyProject: (data: any, token?: string) => 
    apiRequest('/developers/my-projects', { method: 'POST', body: JSON.stringify(data), headers: token ? { Authorization: `Bearer ${token}` } : {} }),
  
  getMyLeads: (token?: string) => 
    apiRequest('/developers/my-leads', { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
  
  getDirectory: () => 
    apiRequest('/developers/directory'),
  
  getPublicDeveloper: (slug: string) => 
    apiRequest(`/developers/public/${slug}`),
  
  submitEnquiry: (data: { developer_id?: number; project_slug?: string; project_name?: string; buyer_name: string; buyer_phone: string; buyer_email?: string; budget?: string; message?: string; enquiry_type?: string }) => 
    apiRequest('/developers/public/enquire', { method: 'POST', body: JSON.stringify(data) })
};
